import { ConfigStatusDot } from "@/components/StatusIcon";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { formatCurrency } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	Check,
	Copy,
	CreditCard,
	FileText,
	HardDrive,
	Loader2,
	Package,
	Pause,
	Play,
	Plus,
	ShoppingCart,
	Trash2,
	UserPlus,
	Users,
	Workflow,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/workspaces/$id")({
	component: WorkspaceDetail,
});

const adminClient = createClient(AdminService, transport);

function formatBytes(bytes: bigint): string {
	const num = Number(bytes);
	if (num < 1024) return `${num} B`;
	if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
	if (num < 1024 * 1024 * 1024) return `${(num / (1024 * 1024)).toFixed(1)} MB`;
	return `${(num / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function WorkspaceDetail() {
	const { t } = useTranslation("admin");
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState("");
	const [slug, setSlug] = useState("");
	const [plan, setPlan] = useState("");

	const [limitProducts, setLimitProducts] = useState("");
	const [limitOrders, setLimitOrders] = useState("");
	const [limitStorage, setLimitStorage] = useState("");
	const [limitMembers, setLimitMembers] = useState("");

	const [inviteOpen, setInviteOpen] = useState(false);
	const [inviteRole, setInviteRole] = useState("");
	const [inviteExpiry, setInviteExpiry] = useState("7");
	const [inviteUrl, setInviteUrl] = useState("");
	const [copied, setCopied] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "workspace", id],
		queryFn: () =>
			adminClient.getWorkspaceDetails({
				workspaceId: id,
			}),
	});

	const { data: plansData } = useQuery({
		queryKey: ["admin", "billing-plans"],
		queryFn: () => adminClient.listBillingPlans({}),
	});

	const ws = data?.workspace;

	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("workspaces_title"), href: "/admin/workspaces" },
		{ label: ws?.name ?? "..." },
	]);

	const updateMutation = useMutation({
		mutationFn: () => {
			const settings: Record<string, unknown> = {};
			if (limitProducts) settings.max_products = Number(limitProducts);
			if (limitOrders) settings.max_orders = Number(limitOrders);
			if (limitStorage) settings.max_storage_gb = Number(limitStorage);
			if (limitMembers) settings.max_members = Number(limitMembers);

			return adminClient.updateWorkspace({
				workspaceId: id,
				name: name || undefined,
				slug: slug || undefined,
				plan: plan || undefined,
				settings:
					Object.keys(settings).length > 0 ? (settings as any) : undefined,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace", id],
			});
			setEditing(false);
		},
	});

	const suspendMutation = useMutation({
		mutationFn: () =>
			adminClient.suspendWorkspace({
				workspaceId: id,
				reason: "Admin suspended",
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace", id],
			});
		},
	});

	const reactivateMutation = useMutation({
		mutationFn: () =>
			adminClient.reactivateWorkspace({
				workspaceId: id,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace", id],
			});
		},
	});

	const inviteMutation = useMutation({
		mutationFn: () =>
			adminClient.generateInviteLink({
				workspaceId: id,
				role: inviteRole,
				expiresInDays: Number.parseInt(inviteExpiry),
			}),
		onSuccess: (data) => {
			setInviteUrl(`${window.location.origin}${data.inviteUrl}`);
		},
	});

	const copyInvite = () => {
		navigator.clipboard.writeText(inviteUrl);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-8 w-48" />
				<div className="grid gap-4 md:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-24" />
					))}
				</div>
			</div>
		);
	}

	if (!ws) {
		return (
			<div className="text-center py-12">
				<p className="text-muted-foreground">{t("workspace_not_found")}</p>
				<Button asChild className="mt-4">
					<Link to="/admin/workspaces">{t("common:back")}</Link>
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-end gap-2">
				<ConfigStatusDot
					color={
						ws.status === "active"
							? "hsl(var(--chart-2))"
							: ws.status === "suspended"
								? "hsl(var(--destructive))"
								: ws.status === "trial"
									? "#6366f1"
									: "hsl(var(--muted-foreground))"
					}
					label={
						ws.status === "active"
							? t("workspaces_status_label_active")
							: ws.status === "suspended"
								? t("workspaces_status_label_suspended")
								: ws.status === "trial"
									? t("workspaces_status_label_trial")
									: t("workspaces_status_label_churned")
					}
				/>
				<Dialog
					open={inviteOpen}
					onOpenChange={(open) => {
						setInviteOpen(open);
						if (!open) {
							setInviteUrl("");
							setCopied(false);
						}
					}}
				>
					<DialogTrigger asChild>
						<Button variant="outline" size="sm">
							<UserPlus className="h-4 w-4 mr-1" />
							{t("workspace_invite")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("workspace_invite_title")}</DialogTitle>
							<DialogDescription>
								{t("workspace_invite_description", { name: ws.name })}
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label>{t("workspace_invite_role")}</Label>
								<Select value={inviteRole} onValueChange={setInviteRole}>
									<SelectTrigger>
										<SelectValue
											placeholder={t("workspace_invite_role_placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										{data.roles.map((role) => (
											<SelectItem key={role.name} value={role.name}>
												{role.displayName}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>{t("workspace_invite_expiry")}</Label>
								<Select value={inviteExpiry} onValueChange={setInviteExpiry}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="1">
											{t("workspace_invite_expiry_1")}
										</SelectItem>
										<SelectItem value="7">
											{t("workspace_invite_expiry_7")}
										</SelectItem>
										<SelectItem value="14">
											{t("workspace_invite_expiry_14")}
										</SelectItem>
										<SelectItem value="30">
											{t("workspace_invite_expiry_30")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
							{inviteUrl ? (
								<div className="space-y-2">
									<Label>{t("workspace_invite_link")}</Label>
									<div className="flex gap-2">
										<Input value={inviteUrl} readOnly />
										<Button variant="outline" size="icon" onClick={copyInvite}>
											{copied ? (
												<Check className="h-4 w-4" />
											) : (
												<Copy className="h-4 w-4" />
											)}
										</Button>
									</div>
								</div>
							) : (
								<Button
									onClick={() => inviteMutation.mutate()}
									disabled={inviteMutation.isPending || !inviteRole}
									className="w-full"
								>
									{inviteMutation.isPending
										? t("workspace_invite_creating")
										: t("workspace_invite_create")}
								</Button>
							)}
						</div>
					</DialogContent>
				</Dialog>
				{ws.status === "suspended" ? (
					<Button
						variant="outline"
						size="sm"
						onClick={() => reactivateMutation.mutate()}
						disabled={reactivateMutation.isPending}
					>
						<Play className="h-4 w-4 mr-1" />
						{t("workspace_activate")}
					</Button>
				) : (
					<Button
						variant="outline"
						size="sm"
						onClick={() => suspendMutation.mutate()}
						disabled={suspendMutation.isPending}
					>
						<Pause className="h-4 w-4 mr-1" />
						{t("workspace_suspend")}
					</Button>
				)}
			</div>

			<Tabs defaultValue="overview" className="mt-2">
				<TabsList>
					<TabsTrigger value="overview">{t("ws_tab_overview")}</TabsTrigger>
					<TabsTrigger value="settings">{t("ws_tab_settings")}</TabsTrigger>
					<TabsTrigger value="billing">{t("ws_tab_billing")}</TabsTrigger>
					<TabsTrigger value="features">{t("ws_tab_features")}</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="space-y-6 mt-4">
					<div className="grid gap-4 md:grid-cols-4">
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									{t("workspace_products")}
								</CardTitle>
								<Package className="h-4 w-4 text-muted-foreground" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">
									{ws.productCount.toLocaleString()}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									{t("workspace_orders")}
								</CardTitle>
								<ShoppingCart className="h-4 w-4 text-muted-foreground" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">
									{ws.orderCount.toLocaleString()}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									{t("workspace_members")}
								</CardTitle>
								<Users className="h-4 w-4 text-muted-foreground" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">{ws.memberCount}</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-sm font-medium">
									{t("workspace_storage")}
								</CardTitle>
								<HardDrive className="h-4 w-4 text-muted-foreground" />
							</CardHeader>
							<CardContent>
								<div className="text-2xl font-bold">
									{formatBytes(ws.storageBytes)}
								</div>
							</CardContent>
						</Card>
					</div>

					{data.pipelineUsage.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Workflow className="h-5 w-5" />
									{t("workspace_pipelines")}
								</CardTitle>
								<CardDescription>
									{t("workspace_pipelines_description")}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="rounded-lg border overflow-hidden">
									<Table>
										<TableHeader>
											<TableRow className="bg-muted/30">
												<TableHead>
													{t("workspace_pipeline_col_name")}
												</TableHead>
												<TableHead className="text-right">
													{t("workspace_pipeline_col_runs")}
												</TableHead>
												<TableHead className="text-right">
													{t("workspace_pipeline_col_success")}
												</TableHead>
												<TableHead className="text-right">
													{t("workspace_pipeline_col_errors")}
												</TableHead>
												<TableHead className="text-right">
													{t("workspace_pipeline_col_rows")}
												</TableHead>
												<TableHead>
													{t("workspace_pipeline_col_last_run")}
												</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{data.pipelineUsage.map((p) => (
												<TableRow
													key={p.pipelineName}
													className="hover:bg-muted/50"
												>
													<TableCell className="font-medium">
														{p.pipelineName}
													</TableCell>
													<TableCell className="text-right">
														{p.totalRuns.toLocaleString()}
													</TableCell>
													<TableCell className="text-right">
														{p.completedRuns.toLocaleString()}
													</TableCell>
													<TableCell className="text-right">
														{Number(p.failedRuns) > 0 ? (
															<span className="text-destructive">
																{p.failedRuns.toLocaleString()}
															</span>
														) : (
															"0"
														)}
													</TableCell>
													<TableCell className="text-right">
														{p.totalRowsProcessed.toLocaleString()}
													</TableCell>
													<TableCell className="text-sm text-muted-foreground">
														{p.lastRunAt
															? new Date(
																	Number(p.lastRunAt.seconds) * 1000,
																).toLocaleString()
															: "—"}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</CardContent>
						</Card>
					)}
				</TabsContent>

				<TabsContent value="settings" className="space-y-6 mt-4">
					<Card>
						<CardHeader>
							<CardTitle>{t("workspace_settings")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{editing ? (
								<>
									<div className="space-y-2">
										<Label>{t("workspace_settings_name")}</Label>
										<Input
											value={name}
											onChange={(e) => setName(e.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("workspace_settings_slug")}</Label>
										<Input
											value={slug}
											onChange={(e) => setSlug(e.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("workspace_settings_plan")}</Label>
										<Select value={plan} onValueChange={setPlan}>
											<SelectTrigger>
												<SelectValue
													placeholder={t("workspace_settings_plan_placeholder")}
												/>
											</SelectTrigger>
											<SelectContent>
												{(plansData?.plans ?? []).map((p) => (
													<SelectItem key={p.slug} value={p.slug}>
														{p.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className="pt-2 border-t">
										<Label className="text-sm font-medium">
											{t("workspace_settings_limits")}
										</Label>
										<div className="grid gap-3 md:grid-cols-2 mt-2">
											<div className="space-y-1">
												<Label className="text-xs text-muted-foreground">
													{t("workspace_settings_limit_products")}
												</Label>
												<Input
													type="number"
													value={limitProducts}
													onChange={(e) => setLimitProducts(e.target.value)}
													placeholder="0 = \u221E"
												/>
											</div>
											<div className="space-y-1">
												<Label className="text-xs text-muted-foreground">
													{t("workspace_settings_limit_orders")}
												</Label>
												<Input
													type="number"
													value={limitOrders}
													onChange={(e) => setLimitOrders(e.target.value)}
													placeholder="0 = \u221E"
												/>
											</div>
											<div className="space-y-1">
												<Label className="text-xs text-muted-foreground">
													{t("workspace_settings_limit_storage")}
												</Label>
												<Input
													type="number"
													value={limitStorage}
													onChange={(e) => setLimitStorage(e.target.value)}
													placeholder="0 = \u221E"
												/>
											</div>
											<div className="space-y-1">
												<Label className="text-xs text-muted-foreground">
													{t("workspace_settings_limit_members")}
												</Label>
												<Input
													type="number"
													value={limitMembers}
													onChange={(e) => setLimitMembers(e.target.value)}
													placeholder="0 = \u221E"
												/>
											</div>
										</div>
									</div>

									<div className="flex gap-2">
										<Button
											onClick={() => updateMutation.mutate()}
											disabled={updateMutation.isPending}
										>
											{updateMutation.isPending && (
												<Loader2 className="h-4 w-4 animate-spin mr-1" />
											)}
											{t("common:save")}
										</Button>
										<Button variant="outline" onClick={() => setEditing(false)}>
											{t("common:cancel")}
										</Button>
									</div>
								</>
							) : (
								<>
									<div className="grid gap-4 md:grid-cols-3">
										<div>
											<Label className="text-muted-foreground">
												{t("workspace_settings_name")}
											</Label>
											<p className="font-medium">{ws.name}</p>
										</div>
										<div>
											<Label className="text-muted-foreground">
												{t("workspace_settings_slug")}
											</Label>
											<p className="font-medium">{ws.slug}</p>
										</div>
										<div>
											<Label className="text-muted-foreground">
												{t("workspace_settings_plan")}
											</Label>
											<p className="font-medium">{ws.plan || "—"}</p>
										</div>
									</div>

									{ws.settings && Object.keys(ws.settings).length > 0 && (
										<div className="pt-2 border-t">
											<Label className="text-sm font-medium text-muted-foreground">
												{t("workspace_settings_limits")}
											</Label>
											<div className="grid gap-2 md:grid-cols-4 mt-2">
												{(ws.settings as any).max_products != null && (
													<div>
														<Label className="text-xs text-muted-foreground">
															{t("workspace_settings_limit_products")}
														</Label>
														<p className="font-medium">
															{Number(
																(ws.settings as any).max_products,
															).toLocaleString()}
														</p>
													</div>
												)}
												{(ws.settings as any).max_orders != null && (
													<div>
														<Label className="text-xs text-muted-foreground">
															{t("workspace_settings_limit_orders")}
														</Label>
														<p className="font-medium">
															{Number(
																(ws.settings as any).max_orders,
															).toLocaleString()}
														</p>
													</div>
												)}
												{(ws.settings as any).max_storage_gb != null && (
													<div>
														<Label className="text-xs text-muted-foreground">
															{t("workspace_settings_limit_storage")}
														</Label>
														<p className="font-medium">
															{Number(
																(ws.settings as any).max_storage_gb,
															).toLocaleString()}
														</p>
													</div>
												)}
												{(ws.settings as any).max_members != null && (
													<div>
														<Label className="text-xs text-muted-foreground">
															{t("workspace_settings_limit_members")}
														</Label>
														<p className="font-medium">
															{Number(
																(ws.settings as any).max_members,
															).toLocaleString()}
														</p>
													</div>
												)}
											</div>
										</div>
									)}

									<Button
										variant="outline"
										onClick={() => {
											setName(ws.name);
											setSlug(ws.slug);
											setPlan(ws.plan);
											const s = ws.settings as any;
											setLimitProducts(
												s?.max_products != null ? String(s.max_products) : "",
											);
											setLimitOrders(
												s?.max_orders != null ? String(s.max_orders) : "",
											);
											setLimitStorage(
												s?.max_storage_gb != null
													? String(s.max_storage_gb)
													: "",
											);
											setLimitMembers(
												s?.max_members != null ? String(s.max_members) : "",
											);
											setEditing(true);
										}}
									>
										{t("workspace_settings_edit")}
									</Button>
								</>
							)}
						</CardContent>
					</Card>

					{ws.suspendedReason && (
						<Card className="border-destructive">
							<CardHeader>
								<CardTitle className="text-destructive">
									{t("workspace_suspended_reason")}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<p>{ws.suspendedReason}</p>
								{ws.suspendedAt && (
									<p className="text-sm text-muted-foreground mt-2">
										{t("workspace_suspended_at", {
											date: new Date(
												Number(ws.suspendedAt.seconds) * 1000,
											).toLocaleString(),
										})}
									</p>
								)}
							</CardContent>
						</Card>
					)}
				</TabsContent>

				<TabsContent value="billing" className="mt-4">
					<BillingSection workspaceId={id} />
				</TabsContent>

				<TabsContent value="features" className="mt-4">
					<EntitlementsTab workspaceId={id} />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// -- Billing section ---------------------------------------------------------

function formatDate(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU");
}

function BillingSection({ workspaceId }: { workspaceId: string }) {
	const { t } = useTranslation("admin");

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<CreditCard className="h-5 w-5" />
					{t("billing_section_title")}
				</CardTitle>
				<CardDescription>{t("billing_section_description")}</CardDescription>
			</CardHeader>
			<CardContent>
				<Tabs defaultValue="subscription">
					<TabsList>
						<TabsTrigger value="subscription">
							{t("billing_tab_subscription")}
						</TabsTrigger>
						<TabsTrigger value="usage">{t("billing_tab_usage")}</TabsTrigger>
						<TabsTrigger value="invoices">
							{t("billing_tab_invoices")}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="subscription" className="mt-4">
						<SubscriptionTab workspaceId={workspaceId} />
					</TabsContent>

					<TabsContent value="usage" className="mt-4">
						<UsageTab workspaceId={workspaceId} />
					</TabsContent>

					<TabsContent value="invoices" className="mt-4">
						<InvoicesTab workspaceId={workspaceId} />
					</TabsContent>
				</Tabs>
			</CardContent>
		</Card>
	);
}

function SubscriptionTab({ workspaceId }: { workspaceId: string }) {
	const { t } = useTranslation("admin");
	const queryClient = useQueryClient();
	const [changePlanOpen, setChangePlanOpen] = useState(false);
	const [newPlanSlug, setNewPlanSlug] = useState("");
	const [newSeats, setNewSeats] = useState("");
	const [addOverrideOpen, setAddOverrideOpen] = useState(false);
	const [overrideType, setOverrideType] = useState<
		"feature" | "plugin" | "metric"
	>("feature");
	const [overrideSlug, setOverrideSlug] = useState("");
	const [overrideQty, setOverrideQty] = useState("");
	const [overrideNotes, setOverrideNotes] = useState("");

	const STATUS_LABELS: Record<string, string> = {
		active: t("subscription_status_active"),
		trialing: t("subscription_status_trialing"),
		past_due: t("subscription_status_past_due"),
		canceled: t("subscription_status_canceled"),
		suspended: t("subscription_status_suspended"),
		draft: t("invoices_status_draft"),
		finalized: t("invoices_status_finalized"),
		sent: t("invoices_status_sent"),
		paid: t("invoices_status_paid"),
		void: t("invoices_status_void"),
	};

	const STATUS_VARIANT: Record<
		string,
		"default" | "secondary" | "destructive" | "outline"
	> = {
		active: "default",
		trialing: "secondary",
		past_due: "destructive",
		canceled: "outline",
		paid: "default",
		void: "outline",
		draft: "secondary",
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "workspace-subscription", workspaceId],
		queryFn: () => adminClient.getWorkspaceSubscription({ workspaceId }),
	});

	const { data: plansData } = useQuery({
		queryKey: ["admin", "billing-plans"],
		queryFn: () => adminClient.listBillingPlans({}),
	});

	const updateMutation = useMutation({
		mutationFn: () =>
			adminClient.updateSubscription({
				subscriptionId: data?.subscription?.id ?? "",
				planSlug: newPlanSlug || undefined,
				seatCount: newSeats ? Number.parseInt(newSeats) : undefined,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-subscription", workspaceId],
			});
			setChangePlanOpen(false);
		},
	});

	const cancelMutation = useMutation({
		mutationFn: () =>
			adminClient.cancelSubscription({
				subscriptionId: data?.subscription?.id ?? "",
			}),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-subscription", workspaceId],
			}),
	});

	const addOverrideMutation = useMutation({
		mutationFn: () =>
			adminClient.addSubscriptionOverride({
				subscriptionId: data?.subscription?.id ?? "",
				featureSlug: overrideType === "feature" ? overrideSlug : "",
				pluginSlug: overrideType === "plugin" ? overrideSlug : "",
				metricCode: overrideType === "metric" ? overrideSlug : "",
				includedQuantity: overrideQty ? BigInt(overrideQty) : BigInt(0),
				notes: overrideNotes,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-subscription", workspaceId],
			});
			setAddOverrideOpen(false);
			setOverrideSlug("");
			setOverrideQty("");
			setOverrideNotes("");
		},
	});

	const removeOverrideMutation = useMutation({
		mutationFn: (overrideId: string) =>
			adminClient.removeSubscriptionOverride({ overrideId }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-subscription", workspaceId],
			}),
	});

	const createSubMutation = useMutation({
		mutationFn: () =>
			adminClient.createSubscription({
				workspaceId,
				planSlug: newPlanSlug,
				period: "monthly",
				seatCount: Number.parseInt(newSeats) || 1,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-subscription", workspaceId],
			});
			setChangePlanOpen(false);
		},
	});

	if (isLoading) return <Skeleton className="h-32" />;

	const sub = data?.subscription;

	if (!sub) {
		return (
			<div className="space-y-4">
				<p className="text-sm text-muted-foreground">
					{t("subscription_no_active")}
				</p>
				<Dialog open={changePlanOpen} onOpenChange={setChangePlanOpen}>
					<DialogTrigger asChild>
						<Button size="sm">
							<Plus className="h-4 w-4 mr-1" />
							{t("subscription_create")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("subscription_create_title")}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label>{t("subscription_plan")}</Label>
								<Select value={newPlanSlug} onValueChange={setNewPlanSlug}>
									<SelectTrigger>
										<SelectValue
											placeholder={t("subscription_plan_placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										{(plansData?.plans ?? []).map((p) => (
											<SelectItem key={p.slug} value={p.slug}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>{t("subscription_seats")}</Label>
								<Input
									type="number"
									value={newSeats}
									onChange={(e) => setNewSeats(e.target.value)}
									placeholder="1"
								/>
							</div>
						</div>
						<DialogFooter>
							<Button
								onClick={() => createSubMutation.mutate()}
								disabled={createSubMutation.isPending || !newPlanSlug}
							>
								{createSubMutation.isPending && (
									<Loader2 className="h-4 w-4 animate-spin mr-1" />
								)}
								{t("common:create")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="grid gap-4 md:grid-cols-3">
				<div>
					<Label className="text-muted-foreground">
						{t("subscription_plan")}
					</Label>
					<p className="font-medium">{sub.planName}</p>
				</div>
				<div>
					<Label className="text-muted-foreground">
						{t("subscription_status")}
					</Label>
					<div>
						<Badge variant={STATUS_VARIANT[sub.status] ?? "secondary"}>
							{STATUS_LABELS[sub.status] ?? sub.status}
						</Badge>
					</div>
				</div>
				<div>
					<Label className="text-muted-foreground">
						{t("subscription_seats")}
					</Label>
					<p className="font-medium">{sub.seatCount}</p>
				</div>
				<div>
					<Label className="text-muted-foreground">
						{t("subscription_period")}
					</Label>
					<p className="font-medium">
						{sub.period === "annual"
							? t("subscription_period_annual")
							: t("subscription_period_monthly")}
					</p>
				</div>
				<div>
					<Label className="text-muted-foreground">
						{t("subscription_current_period")}
					</Label>
					<p className="text-sm">
						{formatDate(sub.currentPeriodStart)} —{" "}
						{formatDate(sub.currentPeriodEnd)}
					</p>
				</div>
				{sub.trialEnd && (
					<div>
						<Label className="text-muted-foreground">
							{t("subscription_trial_until")}
						</Label>
						<p className="text-sm">{formatDate(sub.trialEnd)}</p>
					</div>
				)}
			</div>

			<div className="flex gap-2">
				<Dialog open={changePlanOpen} onOpenChange={setChangePlanOpen}>
					<DialogTrigger asChild>
						<Button variant="outline" size="sm">
							{t("subscription_change_plan")}
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{t("subscription_change_title")}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4 pt-2">
							<div className="space-y-2">
								<Label>{t("subscription_plan")}</Label>
								<Select value={newPlanSlug} onValueChange={setNewPlanSlug}>
									<SelectTrigger>
										<SelectValue
											placeholder={t("subscription_plan_placeholder")}
										/>
									</SelectTrigger>
									<SelectContent>
										{(plansData?.plans ?? []).map((p) => (
											<SelectItem key={p.slug} value={p.slug}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>{t("subscription_seats")}</Label>
								<Input
									type="number"
									value={newSeats}
									onChange={(e) => setNewSeats(e.target.value)}
									placeholder={String(sub.seatCount)}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button
								onClick={() => updateMutation.mutate()}
								disabled={updateMutation.isPending}
							>
								{updateMutation.isPending && (
									<Loader2 className="h-4 w-4 animate-spin mr-1" />
								)}
								{t("common:save")}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{sub.status !== "canceled" && (
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="outline" size="sm" className="text-destructive">
								{t("subscription_cancel")}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t("subscription_cancel_title")}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("subscription_cancel_description")}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>{t("common:back")}</AlertDialogCancel>
								<AlertDialogAction
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									onClick={() => cancelMutation.mutate()}
								>
									{t("subscription_cancel")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}
			</div>

			{/* Overrides */}
			<div className="pt-4 border-t">
				<div className="flex items-center justify-between mb-3">
					<h3 className="text-sm font-medium">{t("override_title")}</h3>
					<Dialog open={addOverrideOpen} onOpenChange={setAddOverrideOpen}>
						<DialogTrigger asChild>
							<Button variant="outline" size="sm">
								<Plus className="h-3.5 w-3.5 mr-1" />
								{t("common:add")}
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>{t("override_add_title")}</DialogTitle>
							</DialogHeader>
							<div className="space-y-4 pt-2">
								<div className="space-y-2">
									<Label>{t("override_type")}</Label>
									<Select
										value={overrideType}
										onValueChange={(v) =>
											setOverrideType(v as typeof overrideType)
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="feature">
												{t("override_type_feature")}
											</SelectItem>
											<SelectItem value="plugin">
												{t("override_type_plugin")}
											</SelectItem>
											<SelectItem value="metric">
												{t("override_type_metric")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>
										{overrideType === "feature"
											? t("override_feature_slug")
											: overrideType === "plugin"
												? t("override_plugin_slug")
												: t("override_metric_code")}
									</Label>
									<Input
										value={overrideSlug}
										onChange={(e) => setOverrideSlug(e.target.value)}
									/>
								</div>
								{overrideType === "metric" && (
									<div className="space-y-2">
										<Label>{t("override_limit")}</Label>
										<Input
											type="number"
											value={overrideQty}
											onChange={(e) => setOverrideQty(e.target.value)}
										/>
									</div>
								)}
								<div className="space-y-2">
									<Label>{t("override_note")}</Label>
									<Input
										value={overrideNotes}
										onChange={(e) => setOverrideNotes(e.target.value)}
									/>
								</div>
							</div>
							<DialogFooter>
								<Button
									onClick={() => addOverrideMutation.mutate()}
									disabled={addOverrideMutation.isPending || !overrideSlug}
								>
									{addOverrideMutation.isPending && (
										<Loader2 className="h-4 w-4 animate-spin mr-1" />
									)}
									{t("common:add")}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>

				{sub.overrides.length === 0 ? (
					<p className="text-sm text-muted-foreground">{t("override_empty")}</p>
				) : (
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead>{t("override_col_type")}</TableHead>
									<TableHead>{t("override_col_slug")}</TableHead>
									<TableHead>{t("override_col_limit")}</TableHead>
									<TableHead>{t("override_col_note")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{sub.overrides.map((o) => (
									<TableRow key={o.id} className="hover:bg-muted/50">
										<TableCell>
											{o.featureSlug
												? t("override_type_feature")
												: o.pluginSlug
													? t("override_type_plugin")
													: t("override_type_metric")}
										</TableCell>
										<TableCell className="font-mono text-sm">
											{o.featureSlug || o.pluginSlug || o.metricCode}
										</TableCell>
										<TableCell>
											{o.includedQuantity
												? Number(o.includedQuantity).toLocaleString()
												: "—"}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{o.notes || "—"}
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 text-destructive"
												onClick={() => removeOverrideMutation.mutate(o.id)}
												disabled={removeOverrideMutation.isPending}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</div>
		</div>
	);
}

function UsageTab({ workspaceId }: { workspaceId: string }) {
	const { t } = useTranslation("admin");

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "workspace-usage", workspaceId],
		queryFn: () => adminClient.getWorkspaceUsage({ workspaceId }),
	});

	if (isLoading) return <Skeleton className="h-32" />;

	const metrics = data?.metrics ?? [];

	if (metrics.length === 0) {
		return <p className="text-sm text-muted-foreground">{t("usage_empty")}</p>;
	}

	return (
		<div className="space-y-3">
			{metrics.map((m) => {
				const included = Number(m.includedQuantity);
				const current = Number(m.currentUsage);
				const pct =
					included > 0 ? Math.min(100, (current / included) * 100) : 0;
				const isOverLimit = m.grantType === "limit" && current >= included;

				return (
					<div key={m.metricCode} className="space-y-1.5">
						<div className="flex items-center justify-between text-sm">
							<span className="font-medium">{m.metricName}</span>
							<span
								className={cn(
									"tabular-nums",
									isOverLimit && "text-destructive font-medium",
								)}
							>
								{current.toLocaleString()} / {included.toLocaleString()}
							</span>
						</div>
						<div className="h-2 rounded-full bg-muted overflow-hidden">
							<div
								className={cn(
									"h-full rounded-full transition-all",
									pct >= 90
										? "bg-destructive"
										: pct >= 70
											? "bg-yellow-500"
											: "bg-primary",
								)}
								style={{ width: `${pct}%` }}
							/>
						</div>
						<div className="flex items-center justify-between text-xs text-muted-foreground">
							<span>
								{m.grantType === "metered"
									? t("usage_metered")
									: t("usage_hard_limit")}
							</span>
							{m.grantType === "metered" && Number(m.overageUnitPrice) > 0 && (
								<span>
									{t("usage_overage", {
										price: formatCurrency(m.overageUnitPrice),
									})}
								</span>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}

function InvoicesTab({ workspaceId }: { workspaceId: string }) {
	const { t } = useTranslation("admin");
	const queryClient = useQueryClient();

	const STATUS_LABELS: Record<string, string> = {
		draft: t("invoices_status_draft"),
		finalized: t("invoices_status_finalized"),
		sent: t("invoices_status_sent"),
		paid: t("invoices_status_paid"),
		void: t("invoices_status_void"),
	};

	const STATUS_VARIANT: Record<
		string,
		"default" | "secondary" | "destructive" | "outline"
	> = {
		paid: "default",
		void: "outline",
		draft: "secondary",
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "workspace-invoices", workspaceId],
		queryFn: () => adminClient.listInvoices({ workspaceId }),
	});

	const generateMutation = useMutation({
		mutationFn: () => adminClient.generateInvoice({ workspaceId }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-invoices", workspaceId],
			}),
	});

	const finalizeMutation = useMutation({
		mutationFn: (invoiceId: string) =>
			adminClient.finalizeInvoice({ invoiceId }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-invoices", workspaceId],
			}),
	});

	const voidMutation = useMutation({
		mutationFn: (invoiceId: string) => adminClient.voidInvoice({ invoiceId }),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-invoices", workspaceId],
			}),
	});

	if (isLoading) return <Skeleton className="h-32" />;

	const invoices = data?.invoices ?? [];

	return (
		<div className="space-y-4">
			<div className="flex justify-end">
				<Button
					variant="outline"
					size="sm"
					onClick={() => generateMutation.mutate()}
					disabled={generateMutation.isPending}
				>
					{generateMutation.isPending ? (
						<Loader2 className="h-4 w-4 animate-spin mr-1" />
					) : (
						<FileText className="h-4 w-4 mr-1" />
					)}
					{t("invoices_generate")}
				</Button>
			</div>

			{invoices.length === 0 ? (
				<p className="text-sm text-muted-foreground text-center py-4">
					{t("invoices_empty")}
				</p>
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead>{t("invoices_col_number")}</TableHead>
								<TableHead>{t("invoices_col_status")}</TableHead>
								<TableHead>{t("invoices_col_period")}</TableHead>
								<TableHead className="text-right">
									{t("invoices_col_amount")}
								</TableHead>
								<TableHead>{t("invoices_col_date")}</TableHead>
								<TableHead />
							</TableRow>
						</TableHeader>
						<TableBody>
							{invoices.map((inv) => (
								<TableRow key={inv.id} className="hover:bg-muted/50">
									<TableCell className="font-mono text-sm">
										{inv.invoiceNumber}
									</TableCell>
									<TableCell>
										<Badge variant={STATUS_VARIANT[inv.status] ?? "secondary"}>
											{STATUS_LABELS[inv.status] ?? inv.status}
										</Badge>
									</TableCell>
									<TableCell className="text-sm">
										{inv.periodStart || "—"} — {inv.periodEnd || "—"}
									</TableCell>
									<TableCell className="text-right font-medium">
										{formatCurrency(inv.total)}
									</TableCell>
									<TableCell className="text-sm text-muted-foreground">
										{formatDate(inv.createdAt)}
									</TableCell>
									<TableCell>
										<div className="flex gap-1">
											<Button variant="ghost" size="sm" asChild>
												<Link
													to={
														`/admin/invoices/${inv.id}` as "/admin/invoices/$id"
													}
													params={{ id: inv.id }}
												>
													{t("invoices_view")}
												</Link>
											</Button>
											{inv.status === "draft" && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => finalizeMutation.mutate(inv.id)}
													disabled={finalizeMutation.isPending}
												>
													{t("invoices_finalize")}
												</Button>
											)}
											{["draft", "finalized", "sent"].includes(inv.status) && (
												<Button
													variant="ghost"
													size="sm"
													className="text-destructive"
													onClick={() => voidMutation.mutate(inv.id)}
													disabled={voidMutation.isPending}
												>
													{t("invoices_void")}
												</Button>
											)}
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}

function EntitlementsTab({ workspaceId }: { workspaceId: string }) {
	const { t } = useTranslation("admin");
	const queryClient = useQueryClient();
	const [saving, setSaving] = useState<string | null>(null);

	const { data: entData, isLoading: entLoading } = useQuery({
		queryKey: ["admin", "workspace-entitlements", workspaceId],
		queryFn: () => adminClient.getWorkspaceEntitlements({ workspaceId }),
	});

	const { data: featData, isLoading: featLoading } = useQuery({
		queryKey: ["admin", "workspace-features", workspaceId],
		queryFn: () => adminClient.getWorkspaceFeatures({ workspaceId }),
	});

	if (entLoading || featLoading) return <Skeleton className="h-32" />;

	const ent = entData?.entitlements;
	const features = featData?.features ?? [];

	async function toggleFeature(slug: string, enabled: boolean) {
		setSaving(slug);
		try {
			await adminClient.setWorkspaceFeature({
				workspaceId,
				featureSlug: slug,
				enabled,
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-entitlements", workspaceId],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "workspace-features", workspaceId],
			});
		} finally {
			setSaving(null);
		}
	}

	return (
		<div className="space-y-6">
			{/* Feature toggles */}
			<div>
				<h3 className="text-sm font-medium mb-3">
					{t("entitlements_features")}
				</h3>
				<div className="space-y-2">
					{features.map((s) => {
						const f = s.feature;
						if (!f) return null;
						const granted =
							ent?.features.find((e) => e.featureSlug === f.slug)?.granted ??
							false;
						const isSaving = saving === f.slug;

						return (
							<div
								key={f.id}
								className={cn(
									"flex items-center justify-between rounded-lg border px-4 py-2.5",
									s.enabled && "border-primary/20 bg-primary/[0.02]",
								)}
							>
								<div className="mr-4">
									<div className="flex items-center gap-2">
										<span className="text-sm font-medium">{f.name}</span>
										{s.enabled && (
											<Badge
												variant="default"
												className="text-[10px] px-1.5 py-0"
											>
												{t("entitlements_active")}
											</Badge>
										)}
										{!granted && (
											<Badge
												variant="outline"
												className="text-[10px] px-1.5 py-0"
											>
												{t("entitlements_not_in_plan")}
											</Badge>
										)}
									</div>
									{f.description && (
										<p className="text-xs text-muted-foreground mt-0.5">
											{f.description}
										</p>
									)}
								</div>

								{s.enabled ? (
									<Button
										variant="outline"
										size="sm"
										disabled={isSaving}
										onClick={() => toggleFeature(f.slug, false)}
									>
										{isSaving ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											t("entitlements_off")
										)}
									</Button>
								) : (
									<Button
										size="sm"
										disabled={isSaving}
										onClick={() => toggleFeature(f.slug, true)}
									>
										{isSaving ? (
											<Loader2 className="h-3.5 w-3.5 animate-spin" />
										) : (
											t("entitlements_on")
										)}
									</Button>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Metrics */}
			{ent && ent.metrics.length > 0 && (
				<div>
					<h3 className="text-sm font-medium mb-3">
						{t("entitlements_metrics")}
					</h3>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead>{t("entitlements_col_metric")}</TableHead>
									<TableHead>{t("entitlements_col_type")}</TableHead>
									<TableHead className="text-right">
										{t("entitlements_col_limit")}
									</TableHead>
									<TableHead className="text-right">
										{t("entitlements_col_used")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{ent.metrics.map((m) => (
									<TableRow key={m.metricCode} className="hover:bg-muted/50">
										<TableCell className="font-medium">
											{m.metricName}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{m.grantType === "metered"
												? t("entitlements_metered")
												: m.grantType === "limit"
													? t("entitlements_hard_limit")
													: m.grantType}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{Number(m.includedQuantity).toLocaleString()}
										</TableCell>
										<TableCell
											className={cn(
												"text-right tabular-nums",
												Number(m.currentUsage) >= Number(m.includedQuantity) &&
													"text-destructive font-medium",
											)}
										>
											{Number(m.currentUsage).toLocaleString()}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</div>
			)}
		</div>
	);
}
